<template>
  <div class="container mt-5">
    <h2 class="text-center">View Draw Odds</h2>

    <!-- Form Section -->
    <form @submit.prevent="getDrawOdds" class="mt-4">
      <div class="mb-3">
        <!-- Drop-down: using item-title/item-value for Vuetify 3 -->
        <v-select
          v-model="species"
          :items="formattedSpeciesOptions"
          item-title="label"
          item-value="value"
          label="Select Species"
          outlined
          dense
          required
        />
      </div>
      <div class="mb-3">
        <v-text-field
          v-model="points"
          label="Your Points"
          type="number"
          outlined
          required
        />
      </div>
      <v-checkbox v-model="isResident" label="Are you a resident?" />
      <v-btn ref="getOddsButton" color="primary" block type="submit" :loading="isLoading">
        <span v-if="!isLoading">Get Draw Odds</span>
        <span v-else>Loading...</span>
      </v-btn>
    </form>

    <!-- Filter and Sorting Controls -->
    <div v-if="drawOdds.length > 0" class="mt-4">
      <v-text-field v-model="filter" label="Filter" outlined dense clearable />
      <div class="d-flex align-items-center gap-3">
        <v-select
          v-model="sortBy"
          :items="sortOptions"
          label="Sort By"
          outlined
          dense
          clearable
          class="flex-grow-1"
        />
        <v-checkbox
          v-model="showSavedOnly"
          label="Show Saved Hunts Only"
          class="mt-0"
          hide-details
        />
      </div>
    </div>

    <!-- Results Section -->
    <div v-if="filteredAndSortedOdds.length > 0" class="mt-4">
      <h4 class="text-center">Your Draw Odds</h4>
      
      <!-- Disclaimer -->
      <div class="disclaimer mb-3">
        <p>Disclaimer: Due to the inherent unpredictability of human behavior, the projected applicant numbers and draw odds provided below are probabilistic estimates.<br> While we strive for accuracy, results are not guaranteed.</p>
      </div>

      <div v-for="(result, index) in filteredAndSortedOdds" :key="result.huntName.id">
        <DrawOddsResultCard 
          :result="result" 
          :customClass="index % 2 === 0 ? 'even-card' : 'odd-card'"
          @error="showError"
        />
      </div>
    </div>

    <!-- No Results -->
    <div v-else class="mt-5 text-center">
      <p>No draw odds found for the selected criteria.</p>
    </div>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import DrawOddsResultCard from '@/components/DrawOddsResultCard.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: "ViewDrawOdds",

  components: {
    DrawOddsResultCard,
  },

  props: {
    // The array of drawPoints with the format:
    // {
    //   "id": 12345,
    //   "customer_state_id": "12345",
    //   "species": "General-season Buck Deer",
    //   "nextEligible": "2025",
    //   "expiringYear": "0",
    //   "ineligibleReason": "",
    //   "state": "Utah",
    //   "points": 0
    // }
    drawPoints: {
      type: Array,
      default: () => [],
    },
    selectedSpecies: {
      type: String,
      default: "",
    },
    selectedPoints: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      points: 0,
      species: "", // The actual value that is sent to the API
      // Original list your API expects (i.e. raw backend strings)
      speciesList: [
        "General-season Buck Deer",
        "Buck Deer",
        "LIMITED ENTRY ELK",
        "LIMITED ENTRY PRONGHORN",
        "BISON",
        "DESERT BIGHORN SHEEP",
        "MTN GOAT",
        "BULL MOOSE",
        "ROCKY MTN SHEEP",
        "Bear"
      ],
      isResident: true,
      isLoading: false,
      filter: "",
      showSavedOnly: false,
      sortBy: "Your Projected Odds",
      sortOptions: ["Hunt Popularity","Your Projected Odds"],
      sortOrder: "Descending",
      drawOdds: [],
      // Snackbar
      showSnackbar: false,
      snackbarText: '',
      snackbarColor: 'error',
    };
  },

  computed: {
    ...mapGetters(['getSavedHunts']),
    
    /**
     * We define a dictionary to map the "drawPoints" species (the ones from the API response)
     * to the raw species strings our backend expects. 
     * (We omit "Turkey" and "General-season Buck Deer" because they're not in the drop down.)
     */
    drawPointsToApiSpecies() {
      return {
        "General-season Buck Deer": "General-season Buck Deer",
        "Limited-entry Buck Deer": "Buck Deer",
        "Limited-entry Bull Elk": "LIMITED ENTRY ELK",
        "Limited-entry Buck Pronghorn": "LIMITED ENTRY PRONGHORN",
        "Bison": "BISON",
        "Desert Bighorn Sheep": "DESERT BIGHORN SHEEP",
        "Rocky Mountain Bighorn Sheep": "ROCKY MTN SHEEP",
        "Mountain Goat": "MTN GOAT",
        "Bull Moose": "BULL MOOSE",
        "Bear": "Bear",
        // When I find more, add here
      };
    },

    // Convert each species into { label, value } for the dropdown
    formattedSpeciesOptions() {
      return this.speciesList.map(item => {
        // Special rename for "Buck Deer"
        if (item === "Buck Deer") {
          return {
            value: item,
            label: "* Limited Entry Buck Deer *"
          };
        } else {
          // Title Case each word, e.g. "LIMITED ENTRY ELK" -> "Limited Entry Elk"
          const label = item
            .toLowerCase()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          return { value: item, label };
        }
      });
    },

    filteredAndSortedOdds() {
      let results = [...this.drawOdds];

      // Filtering
      if (this.filter) {
        const filterText = this.filter.toLowerCase();
        results = results.filter((result) => {
          const huntNameValues = Object.values(result.huntName).join(" ").toLowerCase();
          return huntNameValues.includes(filterText);
        });
      }

      // Filter saved hunts
      if (this.showSavedOnly) {
        results = results.filter(result => this.getSavedHunts.includes(result.huntName.id));
      }

      // Sorting
      results.sort((a, b) => {
        let comparison = 0;
        if (this.sortBy === "Your Projected Odds") {
          comparison = b.drawOdds.totalPermitOdds - a.drawOdds.totalPermitOdds;
        } else if (this.sortBy === "Hunt Popularity") {
          comparison = b.drawOdds.projectedNumberOfOtherApplicants - a.drawOdds.projectedNumberOfOtherApplicants;
        }
        return this.sortOrder === "Ascending" ? -comparison : comparison;
      });

      return results;
    },
  },

  watch: {
    // Whenever 'species' changes, see if there's a matching drawPoints item
    species(newValue, oldValue) {
      this.setPointsFromDrawPoints(newValue);
    },
    // Watch for selected species/points from table click
    selectedSpecies: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          console.log('Selected species from table:', newValue);
          
          // Map common variations to their API counterparts
          const speciesMap = {
            "General-season Buck Deer": "General-season Buck Deer",
            "Limited-entry Buck Deer": "Buck Deer",
            "Limited-entry Bull Elk": "LIMITED ENTRY ELK",
            "Limited-entry Buck Pronghorn": "LIMITED ENTRY PRONGHORN",
            "Bull Moose": "BULL MOOSE",
            "Mountain Goat": "MTN GOAT",
            "Rocky Mountain Bighorn Sheep": "ROCKY MTN SHEEP",
            "Desert Bighorn Sheep": "DESERT BIGHORN SHEEP",
            "Bison": "BISON",
            "Bear": "Bear"
          };

          // Try direct mapping first
          let apiSpecies = speciesMap[newValue];

          // If no direct mapping, try normalized comparison
          if (!apiSpecies) {
            const normalizedNewValue = newValue
              .replace("Limited-entry ", "")
              .replace("Bull ", "")
              .replace("Buck ", "")
              .toUpperCase();
            
            apiSpecies = this.speciesList.find(species => {
              const normalizedSpecies = species
                .replace("LIMITED ENTRY ", "")
                .toUpperCase();
              return normalizedNewValue === normalizedSpecies;
            });
          }
          
          if (apiSpecies) {
            this.species = apiSpecies;
            this.points = this.selectedPoints;
            this.getDrawOdds();
          } else {
            console.warn('No mapping found for species:', newValue);
          }
        }
      }
    }
  },

  methods: {
    ...mapActions(["logout"]),

    showError(message) {
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.showSnackbar = true;
    },

    /**
     * Called whenever 'species' changes. 
     * We'll try to find a drawPoint whose species can be mapped to 'newValue' 
     * and then set our points to that drawPoint's points.
     */
    setPointsFromDrawPoints(apiSpeciesValue) {
      // We want to invert our dictionary => find the "drawPoints" species that maps to this API species
      // e.g. if "LIMITED ENTRY ELK" => we want to find "Limited-entry Bull Elk"
      const invertMap = Object.entries(this.drawPointsToApiSpecies).reduce((acc, [dpSpecies, apiVal]) => {
        acc[apiVal] = dpSpecies; 
        return acc;
      }, {});

      // If invertMap["LIMITED ENTRY ELK"] => "Limited-entry Bull Elk"
      const drawPointsSpecies = invertMap[apiSpeciesValue];
      if (!drawPointsSpecies) {
        // Possibly user picked something that doesn't map (like "Buck Deer"? We do handle that though)
        // "Buck Deer" => "Limited-entry Buck Deer"? yes
        // So let's handle that special case:
        if (apiSpeciesValue === "Buck Deer") {
          // Then the "drawPoints" species is "Limited-entry Buck Deer"
          this.setPointsForSpecies("Limited-entry Buck Deer");
        } else {
          this.points = 0; 
        }
        return;
      }
      
      // Set the points for that species from drawPoints
      this.setPointsForSpecies(drawPointsSpecies);
    },

    setPointsForSpecies(drawPointsSpecies) {
      // Handle null/undefined drawPoints
      if (!this.drawPoints) {
        this.points = 0;
        return;
      }
      // Look for a drawPoint with .species == drawPointsSpecies
      const match = this.drawPoints.find(dp => dp.species === drawPointsSpecies);
      if (match) {
        this.points = match.points; 
      } else {
        this.points = 0; // default if none found
      }
    },

    async getDrawOdds() {
      this.isLoading = true;
      try {
        const queryParams = new URLSearchParams({
          species: this.species,   // The actual raw value from speciesList
          year: "2024",
          isResident: this.isResident,
          bonusPoints: this.points,
        });

        const response = await fetch(`${this.$API_BASE_URL}/api/drawodds/species?${queryParams}`, {
          method: "GET",
          credentials: "include",
        });

        if (response.ok) {
          this.drawOdds = await response.json();
          const isSmallScreen = window.innerWidth < 768;
          // If results returned, scroll so the button is at the top
          if (this.drawOdds.length > 0 && isSmallScreen) {
            this.$nextTick(() => {
              // Use smooth scrolling
              const buttonEl = this.$refs.getOddsButton?.$el;
              buttonEl.scrollIntoView({ behavior: 'smooth' });
            });
          }
        } else if (response.status === 403) {
          console.log("invalid session detected. logging out.");
          this.logout();
        }
      } catch (error) {
        console.log("broken session detected. logging out.");
        this.logout();
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.disclaimer {
  background-color: #fffde7;
  border: 1px solid #fdd835;
  border-radius: 4px;
  padding: 8px;
  margin: 10px 0;
}

.disclaimer p {
  color: #f9a825;
  font-size: 0.85em;
  margin: 0;
  font-weight: bold;
}
</style>
